/* eslint-disable no-irregular-whitespace --
   全角空白 (U+3000) を公式データの区切り文字として正規表現・コメントで意図的に扱う。 */
/**
 * 避難先の「表示ラベル」を公式名称から整形する (cleanPlaceLabel)。
 *
 * 公式データ (国土地理院/国土数値情報/東京都防災マップ) の name は最大 5 層を連結する:
 *   [行政接頭辞] [施設名] 　[棟/エリア] [（階条件）] [（※ハザード規模）]
 *   例「横浜市立本町小学校　校舎１３（３階以上）（※洪水は最大規模）」
 * 地図タグは 1 物件=1 避難先のローカル文脈なので、施設名だけの短いラベルで十分。
 * 棟・階・ハザードの区別が要るのは「マスター名 / 選択ドロップダウン / id」で、そちらは温存する
 * (この関数は表示用の派生値だけを作る。id・マスターは書き換えない)。
 *
 * ## 整形ルール (公式データを全 32,845 件精査して確定。詳細は tasks/todo.md)
 * 1. 行政接頭辞 (市立/区立/町立/村立/都立/道立/府立/県立/組合立/国立/私立) を除去。
 * 2. 末尾の学校系構造語 (校舎N/体育館N/校庭/グラウンド/運動場/講堂/屋内運動場/園庭/…) を除去。
 * 3. 括弧注記は「階/浸水/洪水/高潮/津波/内水/規模/開設/大雨/※/運動場/体育館/…」を含むものだけ
 *    除去 (（株）等の法人マーカー・通称・「旧○○」参照は温存)。
 * 4. 「・」連結 (2 施設が同一敷地) は分割して各セグメントを整形し「・」で戻す
 *    (「県立A・県立B」→「A・B」)。括弧は入れ子があるのでループ除去。
 *
 * ## 保護 (機械的に刈ると壊れるもの — 精査で確認)
 * - 分館/支所/出張所/分校/分園 = 別の場所。棟語として刈らない (STRUCT に含めない)。
 * - （株）（有）等の法人マーカー = 社名の一部。ENTITY で温存。
 * - 住宅の「N号棟」等 = 別建物。STRUCT に含めない。
 * - 「○○市立体育館」のように刈ると汎用語だけになる 7 件は行政接頭辞の除去を見送る (GENERIC ガード)。
 * - NFKC は出力に使わない (全角括弧・全角数字・㈱ の字形を保つ)。2 文字未満は原文へフォールバック。
 */

/** 行政接頭辞 (地名 0〜8 字 + 立マーカー)。「・」「空白」は跨がない (先頭の設置者のみ)。 */
const ADMIN =
  /^[^・　\s]{0,8}(市立|区立|町立|村立|都立|道立|府立|県立|組合立|国立|私立)/;

/**
 * 末尾の学校系構造語 (全角/半角空白の後ろ)。分館/支所/N号棟 は意図的に含めない。
 * 棟番号はハイフン付き複合番号 (「校舎１６－３」「校舎９ー２」等) も含む
 * (実測: 横浜市マスター — ハイフン非対応だと棟だけ省略されず候補に残った)。
 */
const STRUCT =
  /[　\s]+(校舎[-0-9０-９－ー]*|体育館[-0-9０-９－ー]*|屋内運動場[^　\s]*|武道場|校庭|グラウンド|運動場|講堂|園庭|プール|アリーナ|多目的[^　\s]*)$/;

/** 入れ子を含まない最内括弧 (全角/半角) を 1 つ拾う。ループで外側まで剥がす。 */
const PAREN = /[（(]([^（()）]*)[）)]/g;

/** 括弧の中身がこれを含めば「注記」= 除去対象 (階・ハザード・運用・構造語)。 */
const ANNO =
  /(階|浸水|洪水|高潮|津波|内水|規模|開設|大雨|※|運動場|体育館|グラウンド|校庭|校舎|講堂|屋内運動|武道|教室|多目的)/;

/** 括弧の中身がこれ「だけ」なら法人マーカー = 温存 (（株）（有）等)。 */
const ENTITY = /^(株|有|財|社|名|学|医|福|宗|独)$/;

/** 整形後にこれ「だけ」になったら刈りすぎ (行政接頭辞の除去を見送る)。 */
const GENERIC =
  /^(体育館|グラウンド|運動場|校庭|会館|センター|プール|広場|駐車場|講堂|ホール|アリーナ|多目的.*)$/;

/** 注記を含む括弧を、入れ子ごと (ループで) 除去する。 */
function stripParens(s: string): string {
  let prev: string;
  do {
    prev = s;
    s = s.replace(PAREN, (m, inner: string) =>
      ENTITY.test(inner.trim()) ? m : ANNO.test(inner) ? '' : m,
    );
  } while (s !== prev);
  return s;
}

/** 末尾の構造語を (複数連なっても) 除去する。 */
function stripStruct(s: string): string {
  let prev: string;
  do {
    prev = s;
    s = s.replace(STRUCT, '');
  } while (s !== prev);
  return s;
}

/** 1 セグメント (「・」で分割した 1 施設分) を整形する。 */
function cleanSegment(seg: string, stripAdmin: boolean): string {
  let n = seg;
  if (stripAdmin) n = n.replace(ADMIN, '');
  n = stripParens(n);
  n = stripStruct(n);
  return n.replace(/^[　\s]+|[　\s]+$/g, '');
}

/**
 * 公式名称 → 地図タグ用の短い表示ラベル。純関数・冪等。
 * 空/2 文字未満になった場合は原文 (trim) を返す (安全網)。
 */
export function cleanPlaceLabel(raw: string): string {
  if (!raw) return raw;
  const out = raw
    .split('・')
    .map((seg) => {
      const c = cleanSegment(seg, true);
      // 行政接頭辞を取ると汎用語だけになるものは、接頭辞を残す (例「○○町立体育館」)。
      return GENERIC.test(c) ? cleanSegment(seg, false) : c;
    })
    .join('・')
    .replace(/[　\s]+([（(])/g, '$1') // 残った通称括弧の前の空白を詰める
    .replace(/[　\s]{2,}/g, '　')
    .replace(/^[　\s]+|[　\s]+$/g, '');
  return out.length < 2 ? raw.trim() : out;
}

/** disambiguatedPlaceLabels の入力 (候補リストの1件分)。 */
export interface PlaceLabelInput {
  name: string;
  municipality?: string | null;
  /**
   * 最終手段の区別子 (例: 距離表示「1.2km」)。原文名まで同一で自治体も無い候補
   * (東京都マスターは municipality を持たない) 同士の衝突にだけ使う。
   */
  hint?: string | null;
}

/** dedupeSameFacility / disambiguatedPlaceLabels が参照する候補の最小形。 */
export interface PlaceCandidateLike {
  id?: string;
  name: string;
  municipality?: string | null;
  point?: { lng: number; lat: number } | null;
}

/** 同一施設とみなす距離 (m)。同一校の棟バリアントは校地内 = この範囲に収まる。 */
const SAME_FACILITY_M = 300;

function pointDistM(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
): number {
  const kx = Math.cos((a.lat * Math.PI) / 180) * 111_320;
  return Math.hypot((b.lng - a.lng) * kx, (b.lat - a.lat) * 110_540);
}

/**
 * 「同一施設の棟・階バリアント」を候補リストから畳む。
 *
 * 整形名 (cleanPlaceLabel) と自治体が同じで、位置も同一校地内 (≤300m) の候補は
 * 同じ施設 — 棟語まで省略して表示する以上、リストに同名を並べる意味が無いので
 * 1 件に統合する (実測: 横浜ランドマークタワーの候補 — 本町小学校の 体育館/校舎１７)。
 * 別施設の同名 (別自治体・同名で遠距離) は残す — 表示の区別は
 * disambiguatedPlaceLabels が付ける。
 *
 * preferredId を渡すと、その候補は統合で消えない (選択済みの棟を保持するため)。
 * 順序は元リストのまま (統合グループは最初に現れた位置に代表が残る)。
 */
export function dedupeSameFacility<T extends PlaceCandidateLike>(
  items: readonly T[],
  preferredId?: string | null,
): T[] {
  const cleaned = items.map((x) => cleanPlaceLabel(x.name));
  const kept: Array<{ item: T; idx: number }> = [];
  const keyOf = (i: number) =>
    `${cleaned[i]}|${(items[i]!.municipality ?? '').trim()}`;
  for (let i = 0; i < items.length; i++) {
    const x = items[i]!;
    const key = keyOf(i);
    const twin = kept.find(
      (k) =>
        keyOf(k.idx) === key &&
        !!k.item.point &&
        !!x.point &&
        pointDistM(k.item.point, x.point) <= SAME_FACILITY_M,
    );
    if (!twin) {
      kept.push({ item: x, idx: i });
      continue;
    }
    // 統合: 選択済み (preferredId) の候補は代表として残す
    if (preferredId && x.id === preferredId && twin.item.id !== preferredId) {
      twin.item = x;
      twin.idx = i;
    }
  }
  return kept
    .sort((a, b) => a.idx - b.idx)
    .map((k) => k.item);
}

/**
 * 候補リスト全体の「表示ラベル」を作る。常に cleanPlaceLabel の整形名
 * (接頭語・棟語とも省略 = 地図タグと同じ形) を使い、整形で同名になる候補が
 * 複数あるときに区別を付ける:
 *   1. 別自治体の同名 (例: 川崎と横浜の「本町小学校」) → 「本町小学校（川崎市）」
 *   2. 自治体で区別できない同名 (自治体なし = 東京都マスター等 / 同一自治体の遠隔
 *      同名) → ヒント (距離)「本町小学校（800m）」
 * 同一施設の棟バリアントはラベルでは区別しない — dedupeSameFacility で先に
 * リスト自体を畳むこと。返り値は items と同じ順序・同じ長さのラベル配列。
 */
export function disambiguatedPlaceLabels(
  items: readonly PlaceLabelInput[],
): string[] {
  const cleaned = items.map((x) => cleanPlaceLabel(x.name));
  const counts = new Map<string, number>();
  for (const c of cleaned) counts.set(c, (counts.get(c) ?? 0) + 1);
  return items.map((x, i) => {
    const base = cleaned[i]!;
    if ((counts.get(base) ?? 0) <= 1) return base;
    const muni = (x.municipality ?? '').trim();
    if (muni) {
      // 同じ (整形名, 自治体) の候補が自分だけなら自治体名で区別できる
      const sameMuni = items.filter(
        (o, j) => cleaned[j] === base && (o.municipality ?? '').trim() === muni,
      ).length;
      if (sameMuni <= 1) return `${base}（${muni}）`;
    }
    // 自治体で区別できない → ヒント (距離) で区別。それも無ければ整形名のまま。
    const hint = (x.hint ?? '').trim();
    return hint ? `${base}（${hint}）` : base;
  });
}
